import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LiveGameItemMobile } from '../lines/live-game-item/live-game-item-mobile';
import { QuickGameItemMobile } from '../lines/quick-game-item/quick-game-item-mobile';
import { useQuickGames } from '../lines/quick-game-item/use-quick-games';
import { WatchGameItemMobile } from '../lines/watch-game-iitem/watch-game-item-mobile';

import { Game } from 'modules/lobby-page/pages/lobby';

import MobileRoomLayout from 'shared/components/layout/mobile-layout';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { RoomFooterText } from 'shared/components/ui/room-footer-text/room-footer-text';
import { useMyGames } from 'shared/hooks/use-build-my-games';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGamesStore } from 'shared/store/games-store';
import { RoomInfo } from 'shared/types/app-types';
import { CurrencyType } from 'shared/web3/utils/getCurrencyInfo';

import ControllerIcon from 'assets/controller.svg?react';
import Loader from 'assets/svg/loader.svg?react';

interface ILiveGamesSectionMobileProps {
  openCreateGame: () => void;
  joinRoomHandler: (roomId: number) => void;
  startGameHandler: (bet: number, wins: number, playerB: string, currency?: CurrencyType) => void;
  games: Game[];
  activeGames: RoomInfo[];
}

export const LiveGamesSectionMobile = ({
  openCreateGame,
  joinRoomHandler,
  startGameHandler,
  games,
  activeGames,
}: ILiveGamesSectionMobileProps) => {
  const { t } = useTranslation();

  const { chainId, address } = useAccount();
  const publicClient = usePublicClient();

  const { availableCurrencies } = useCurrenciesStore();
  const { startStore, activeSingle, activeRooms, clearStore } = useGamesStore();
  const { isLoadingBalances, quickGames, selectedCurrency } = useQuickGames();

  const myGames = useMyGames({ activeRooms, activeSingle, activeGames, address });

  useEffect(() => {
    if (publicClient) {
      startStore(publicClient);
    }
    return clearStore;
  }, [publicClient, chainId]);

  const prioritizeOurGames = (rooms: RoomInfo[], address?: string) =>
    [...rooms].sort((a, b) => {
      const addr = address?.toLowerCase();
      return (
        (b.data.playerA?.toLowerCase() === addr ? 1 : 0) -
        (a.data.playerA?.toLowerCase() === addr ? 1 : 0)
      );
    });

  const renderGamesList = useCallback(() => {
    let sortedRooms = prioritizeOurGames(activeSingle, address).filter(() => true);
    return sortedRooms.map((room) => {
      const currency = availableCurrencies.find(
        (c) => c.address.toLowerCase() === room.data.token.toLowerCase(),
      );
      return (
        <LiveGameItemMobile
          key={`${room.data.roomId.toString()}_${room.data.playerA}`}
          roomId={room.data.roomId}
          address={room.data.playerA as `0x${string}`}
          amount={Number(room.data.bet)}
          currency={currency}
          ourGame={room.data.playerA?.toLowerCase() === address?.toLowerCase()}
          joinRoomHandler={joinRoomHandler}
          status={room.status}
          wins={room.data.wins}
        />
      );
    });
  }, [address, availableCurrencies, isLoadingBalances, chainId, activeSingle, joinRoomHandler]);

  const renderMyGamesList = useCallback(() => {
    if (!myGames.length) return null;
    return myGames.map((room) => {
      const currency = availableCurrencies.find(
        (c) => c.address.toLowerCase() === room.data.token.toLowerCase(),
      );
      return (
        <LiveGameItemMobile
          key={`${room.data.roomId.toString()}_${room.data.playerA}`}
          roomId={room.data.roomId}
          address={room.data.playerA as `0x${string}`}
          amount={Number(room.data.bet)}
          currency={currency}
          joinRoomHandler={joinRoomHandler}
          ourGame
          status={room.status}
          playerB={room.data.playerB}
          wins={room.data.wins}
        />
      );
    });
  }, [
    address,
    availableCurrencies,
    isLoadingBalances,
    selectedCurrency,
    chainId,
    myGames,
    joinRoomHandler,
  ]);

  const renderQuickGameList = useCallback(() => {
    if (isLoadingBalances) {
      return (
        <div className="flex justify-center py-4">
          <Loader className="h-6 w-6 animate-spin fill-[#fff] text-[#FFC000] sm:h-8 sm:w-8" />
        </div>
      );
    }

    return quickGames.map((game) => (
      <QuickGameItemMobile
        key={game.currency.symbol + game.amount + game.wins}
        amount={Number(game.amount)}
        currency={game.currency}
        wins={game.wins}
        startGameHandler={startGameHandler}
      />
    ));
  }, [address, quickGames, availableCurrencies, isLoadingBalances, chainId, startGameHandler]);

  const renderWatchGamesList = useCallback(
    () => games.map((game) => <WatchGameItemMobile key={game[0]} game={game} />),
    [games],
  );

  return (
    <MobileRoomLayout
      title={t('pages.liveGamesSection.title')}
      footer={
        <div className="flex h-full w-full flex-col items-center justify-between gap-1">
          <SecondaryBtn
            text="common.customGame"
            svg={<ControllerIcon className="h-5 w-5 text-white" />}
            onClick={openCreateGame}
            className="w-full"
            showGlare
            data-testid="create-game-button"
          />
          <RoomFooterText />
        </div>
      }
      showFloor={false}
    >
      <div className="w-full">
        <div className="no-scrollbar relative mt-2 flex flex-col gap-2 overflow-scroll overflow-x-hidden sm:gap-4 md:pr-5 lg:mt-5">
          {renderQuickGameList()}
          {renderMyGamesList()}
          {renderGamesList()}
          {games?.length > 0 && (
            <div className="flex items-center justify-center">
              <div className="h-[1px] w-full bg-white/15" />
              <p className="scroll-custom whitespace-nowrap bg-gradient-to-tr from-white via-white to-[#B5E0FF] bg-clip-text px-2.5 text-[10px] font-bold text-transparent text-white/50">
                {t('pages.liveGamesSection.watchGames')}
              </p>
              <div className="h-[1px] w-full bg-white/15" />
            </div>
          )}
          {games?.length > 0 && renderWatchGamesList()}
        </div>
      </div>
    </MobileRoomLayout>
  );
};
